import { useState } from 'react'
import toastService from '@/services/toast/service'
import { onboardingService, type AdminOnboardingCallRecord } from '@/services/onboarding'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useCanEditAppointments } from '@/hooks/useIsViewer'
import { CALL_STATUS_OPTIONS } from './onboarding-labels'

interface EditCallModalProps {
  call: AdminOnboardingCallRecord
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: (updated: AdminOnboardingCallRecord) => void
}

/**
 * Status + notes editor for an onboarding call. The call has ONE note,
 * edited as a plain textarea; saving pushes it (and status changes) to the
 * linked GHL appointment. Notes are one-way: backoffice → GHL.
 */
export default function EditCallModal({ call, open, onOpenChange, onSaved }: EditCallModalProps) {
  const readOnly = !useCanEditAppointments()
  const existingNote = call.notes[0]
  const [status, setStatus] = useState(call.status)
  const [noteText, setNoteText] = useState(existingNote?.body ?? '')
  const [saving, setSaving] = useState(false)

  // A canceled call stays canceled — only the who-canceled attribution can be
  // corrected (GHL-originated cancellations arrive as "by customer").
  const isCanceled = call.status.startsWith('canceled')
  const statusOptions = CALL_STATUS_OPTIONS.filter((o) =>
    isCanceled ? o.value.startsWith('canceled') : true,
  )

  const bookedFor = call.startAtUtc
    ? `${new Date(call.startAtUtc).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: call.timezone ?? undefined,
      })} (${call.timezone ?? 'UTC'})`
    : null

  const handleSave = async () => {
    setSaving(true)
    try {
      let result = await onboardingService.updateCall(call.id, { status })

      const text = noteText.trim()
      const original = existingNote?.body ?? ''
      if (text !== original) {
        if (!text && existingNote) {
          result = await onboardingService.deleteNote(call.id, existingNote.id)
        } else if (existingNote) {
          result = await onboardingService.updateNote(call.id, existingNote.id, text)
        } else {
          result = await onboardingService.addNote(call.id, text)
        }
      }

      onSaved(result.record)
      if (result.ghlSynced) {
        toastService.success('Appointment updated')
      } else {
        toastService.error("Saved, but couldn't sync to GHL")
      }
      onOpenChange(false)
    } catch {
      toastService.error("Couldn't update the appointment")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Edit appointment · {call.agency?.name ?? call.contact?.name ?? 'No account'}</DialogTitle>
          <DialogDescription>
            {call.outcome === 'booked'
              ? `Booked for ${bookedFor ?? 'unknown time'}${call.assignedUserName ? ` · with ${call.assignedUserName}` : ''}`
              : 'The offer was declined.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {call.outcome === 'booked' && (
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus} disabled={readOnly}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>
              Notes <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              disabled={readOnly}
              rows={3}
              maxLength={5000}
              placeholder="Why did they show / not show, what was set up..."
            />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={readOnly || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
